// frontend/src/askos/SamkalpLoginPage.js
//
// Dedicated, slug-free login for Samkalp IAS Academy students.
// Replicates the pattern in examination/pages/ExamLoginPage.js (branding
// preflight via raw axios, own token storage) minus the slug input field —
// the tenant slug is a fixed constant, never shown to the student.
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';

const ACADEMY_SLUG = 'samkalp-ias';

const NAVY = '#1e3a6e';
const BLUE = '#2563eb';
const WHITE = '#ffffff';

const inputStyle = { border: 'none', borderBottom: '2px solid #e2e8f0', borderRadius: 0, padding: '8px 0', boxShadow: 'none', background: 'transparent' };
const labelStyle = { fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#64748b' };

const SamkalpLoginPage = ({ onLogin }) => {
    const [branding, setBranding] = useState({ name: '', logo: '' });
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [mode, setMode] = useState('login'); // login | forgot | sent
    const [forgotEmail, setForgotEmail] = useState('');

    useEffect(() => {
        axios.get(`/public/tenant-branding/${ACADEMY_SLUG}`)
            .then(res => setBranding({ name: res.data.name || '', logo: res.data.logo || '' }))
            .catch(() => setBranding({ name: '', logo: '' }));
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!acceptedTerms) {
            setError('You must accept the Terms of Service and Privacy Policy to continue.');
            return;
        }
        setLoading(true); setError('');
        try {
            const res = await axios.post(
                `/tenant/${ACADEMY_SLUG}/askos/token?terms_accepted=true`,
                new URLSearchParams({ username: email, password }),
                { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
            );
            const { access_token } = res.data;
            localStorage.setItem('askos_token', access_token);
            localStorage.setItem('askos_slug', ACADEMY_SLUG);
            onLogin({ slug: ACADEMY_SLUG, email, access_token });
        } catch (err) {
            setError(err.response?.data?.detail || 'Sign in failed. Please check your email and password.');
        } finally { setLoading(false); }
    };

    const handleForgot = async (e) => {
        e.preventDefault();
        setLoading(true); setError('');
        try {
            await axios.post(`/tenant/${ACADEMY_SLUG}/askos/forgot-password`, { email: forgotEmail });
            setMode('sent');
        } catch (err) {
            setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
        } finally { setLoading(false); }
    };

    return (
        <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY} 45%, ${BLUE} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ width: '100%', maxWidth: 960, background: WHITE, borderRadius: 20, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.22)', display: 'flex', minHeight: 580 }}>

                {/* Left panel — branding */}
                <div style={{ flex: 1, background: `linear-gradient(160deg, ${NAVY}, ${BLUE})`, padding: '48px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: WHITE }}>
                    <div>
                        {branding.logo
                            ? <img src={branding.logo} alt={branding.name} height="60" style={{ objectFit: 'contain', marginBottom: 24 }} onError={e => { e.target.style.display = 'none'; }} />
                            : <div style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: 24 }}>🎓</div>}
                        <h2 style={{ fontWeight: 800, fontSize: '1.8rem', marginBottom: 8 }}>{branding.name || 'Samkalp IAS Academy'}</h2>
                        <p style={{ opacity: 0.75, fontSize: '1rem' }}>Your AI-powered UPSC prep companion.</p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        {[
                            { icon: '📰', title: 'Current Affairs',       desc: 'Daily PIB & The Hindu highlights, curated for UPSC.' },
                            { icon: '💬', title: 'Ask AskOS Anything',   desc: 'Instant, cited answers grounded in your syllabus.' },
                            { icon: '📊', title: 'Track Your Progress',  desc: 'Subject-wise mastery, always up to date.' },
                        ].map(f => (
                            <div key={f.title} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                                <span style={{ fontSize: '1.4rem', flexShrink: 0, marginTop: 2 }}>{f.icon}</span>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>{f.title}</div>
                                    <div style={{ opacity: 0.7, fontSize: '0.8rem', marginTop: 2 }}>{f.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right panel — form */}
                <div style={{ width: 400, padding: '48px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'center', overflowY: 'auto' }}>

                    {mode === 'login' && (
                        <>
                            <h3 style={{ fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>Student Sign In</h3>
                            <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: 28 }}>Access your Samkalp AskOS workspace</p>

                            {error && <Alert variant="danger" className="py-2 small" role="alert">{error}</Alert>}

                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-4">
                                    <Form.Label style={labelStyle}>Email</Form.Label>
                                    <Form.Control type="email" placeholder="student@example.com" value={email} onChange={e => setEmail(e.target.value)} required style={inputStyle} />
                                </Form.Group>
                                <Form.Group className="mb-4">
                                    <Form.Label style={labelStyle}>Password</Form.Label>
                                    <Form.Control type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required style={inputStyle} />
                                </Form.Group>

                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12, marginTop: -8 }}>
                                    <button type="button" style={{ background: 'none', border: 'none', padding: 0, color: '#64748b', fontSize: '0.8rem', cursor: 'pointer' }}
                                        onClick={() => { setError(''); setForgotEmail(email); setMode('forgot'); }}>
                                        Forgot password?
                                    </button>
                                </div>

                                <Form.Group className="mb-4">
                                    <Form.Check type="checkbox" id="samkalp-terms-checkbox" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)}
                                        label={
                                            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                                I agree to the{' '}
                                                <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: BLUE, fontWeight: 600, textDecoration: 'none' }}>Terms of Service</a>
                                                {' '}and{' '}
                                                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: BLUE, fontWeight: 600, textDecoration: 'none' }}>Privacy Policy</a>.
                                            </span>
                                        }
                                    />
                                </Form.Group>

                                <Button type="submit" disabled={loading || !acceptedTerms}
                                    style={{ width: '100%', background: acceptedTerms ? NAVY : '#94a3b8', border: 'none', borderRadius: 10, padding: '13px', fontWeight: 700, fontSize: '1rem' }}>
                                    {loading ? <Spinner animation="border" size="sm" /> : 'Enter AskOS →'}
                                </Button>
                            </Form>
                        </>
                    )}

                    {mode === 'forgot' && (
                        <>
                            <h3 style={{ fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>Reset Password</h3>
                            <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: 28 }}>We'll email you a reset link</p>
                            {error && <Alert variant="danger" className="py-2 small" role="alert">{error}</Alert>}
                            <Form onSubmit={handleForgot}>
                                <Form.Group className="mb-4">
                                    <Form.Label style={labelStyle}>Email</Form.Label>
                                    <Form.Control type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required style={inputStyle} />
                                </Form.Group>
                                <Button type="submit" disabled={loading} style={{ width: '100%', background: NAVY, border: 'none', borderRadius: 10, padding: '13px', fontWeight: 700 }}>
                                    {loading ? <Spinner animation="border" size="sm" /> : 'Send Reset Link'}
                                </Button>
                            </Form>
                            <button type="button" style={{ background: 'none', border: 'none', marginTop: 16, color: '#64748b', fontSize: '0.8rem', cursor: 'pointer' }}
                                onClick={() => { setError(''); setMode('login'); }}>
                                ← Back to sign in
                            </button>
                        </>
                    )}

                    {mode === 'sent' && (
                        <>
                            <h3 style={{ fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>Check your email</h3>
                            <p style={{ color: '#64748b', fontSize: '0.85rem' }}>If an account exists for that email, a reset link is on its way.</p>
                            <button type="button" style={{ background: 'none', border: 'none', marginTop: 16, color: '#64748b', fontSize: '0.8rem', cursor: 'pointer' }}
                                onClick={() => { setError(''); setMode('login'); }}>
                                ← Back to sign in
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SamkalpLoginPage;
